"use client"; // a hook: Next must keep it out of Server Components that import this package for `imageUrl`

import { useState, type ChangeEvent } from "react";
import { uploadFile, uploadFiles, type UploadKind, type UploadedFile } from "./files";

/**
 * Wires a file `<input>` to `uploadFile`. No toast here: the portal has no toast library, so pages
 * toast from `onUploaded`/`onError`. An error thrown by `onUploaded` itself (the follow-up save)
 * lands in `error`/`onError` too.
 */
type Common = { onError?: (error: unknown) => void };
type Single = Common & { multiple?: false; onUploaded: (result: UploadedFile, file: File) => unknown };
type Multiple = Common & { multiple: true; onUploaded: (results: UploadedFile[], files: File[]) => unknown };

export type FileUpload = {
  busy: boolean;
  error: unknown;
  onChange: (event: ChangeEvent<HTMLInputElement>) => Promise<void>;
};

export function useFileUpload(kind: UploadKind, options: Single): FileUpload;
export function useFileUpload(kind: UploadKind, options: Multiple): FileUpload;
export function useFileUpload(kind: UploadKind, options: Single | Multiple): FileUpload {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function onChange(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    event.target.value = ""; // re-picking the same file must still fire a change
    if (files.length === 0) return;

    setError(null);
    setBusy(true);
    try {
      if (options.multiple) await options.onUploaded(await uploadFiles(kind, files), files);
      else await options.onUploaded(await uploadFile(kind, files[0]!), files[0]!);
    } catch (err) {
      setError(err);
      options.onError?.(err);
    } finally {
      setBusy(false);
    }
  }

  return { busy, error, onChange };
}
